import { useState, useCallback } from "react";
import axios, { AxiosRequestConfig, Method } from "axios";
import { apiV1Endpoint } from "../../api/v1";

interface UseApiWithFiltersProps<TParams = any> {
  apiUrl: string;
  method?: Method;
  initialParams?: TParams;
}

interface ApiResponse<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
  fetchData: (filters?: Record<string, any>, singleOnwer?: boolean) => Promise<void>;
  ownerOptions: any[];
}

/**
 * 🔁 A reusable hook for making API calls with optional filters.
 * - Automatically merges `initialParams` with new filters.
 * - Removes empty, null, undefined, or false values.
 * - Works with both GET and POST requests.
 */
export function useApiWithFilters<TData = any, TParams = any>({
  apiUrl,
  method = "GET",
  initialParams = {} as TParams,
}: UseApiWithFiltersProps<TParams>): ApiResponse<TData> {
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerOptions, setOwnerOption] = useState<any>([]);
  const [filters, setFilters] = useState<Record<string, any>>(initialParams);

  // 🧹 Clean out empty or invalid filter values
  const cleanFilters = (obj: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([, value]) =>
          value !== "" && value !== null && value !== undefined && !(typeof value === "boolean" && value === false)
      )
    );
  };

  const fetchData = useCallback(
    async (newFilters: Record<string, any> = {}) => {
      setLoading(true);
      setError(null);

      // ✅ Merge all filters: old + new + initialParams
      const mergedFilters = { ...filters, ...newFilters, ...initialParams };
      const finalFilters = cleanFilters(mergedFilters);
     
      try {
        const config: AxiosRequestConfig = {
          url: `${apiV1Endpoint}${apiUrl}`,
          method,
        };

        if (method.toUpperCase() === "GET") {
          config.params = finalFilters;
        } else {
          config.data = finalFilters;
        }
        const response = await axios(config);
        let ownerOption = [];


        if (newFilters?.singleOnwer) {
           ownerOption?.push({
              id: response?.data?.result?.ownerId,
              name: response?.data?.result?.name,
              email: response?.data?.result?.email,
            });
        } else {
          response?.data?.result?.result?.data?.forEach((element) => {
            ownerOption?.push({
              id: element?._id,
              name: element?.name,
              email: element?.email,
            });
          });
        }

        ownerOption?.length > 0 && setOwnerOption(ownerOption);
        setData(response?.data?.result || response?.data);
        setFilters(finalFilters);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, method, filters]
  );

  return { data, loading, error, fetchData, ownerOptions };
}
